/*
 * PaywallCard — single reusable primitive for HTTP 402 quota responses.
 *
 * Consumes the payload emitted by `subscriptions.QuotaCheck.to_paywall_dict()`
 * on the backend. Mounted anywhere a quota gate may fire:
 *
 *   - Stage 1 picker  → 402 on /api/intake/stage1/pick
 *   - Approach choice → 402 on /api/intake/approach (future)
 *   - Team invite     → 402 on POST /api/workspace/{id}/members
 *
 * Two CTAs, in priority order:
 *
 *   1. Upgrade plan        → navigates to /client/billing (recurring path)
 *   2. Add one (extra use) → POSTs /api/billing/top-up (one-time charge)
 *
 * Brand: Agents Anywhere house — white card on light page, blue primary
 * accent, mono eyebrow, sentence-case body. No emoji.
 */

import React, { useState } from 'react';
import axios, { AxiosError } from 'axios';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, X, Sparkles } from 'lucide-react';

import { usePermissions } from '../../hooks/usePermissions';
import { Action, Modal } from '../aa';
import './PaywallCard.css';


export interface PaywallPayload {
  paywall: true;
  reason: string;
  quota_kind: 'agent_request' | 'consultant_session' | 'team_seat';
  current_usage: number;
  limit: number;
  plan_tier: string;
  plan_display_name: string;
  upgrade_to: string | null;
  message: string;
  top_up: {
    available: boolean;
    unit_price_cents: number | null;
  };
}


interface PaywallCardProps {
  payload: PaywallPayload;
  /** Fires after a successful top-up so the parent can retry the action. */
  onTopUpSuccess?: (creditId: string | null) => void;
  /** Optional dismiss — when omitted the card has no close affordance. */
  onDismiss?: () => void;
  /** Extra wrapper className, e.g. "mt-4" or constraint width. */
  className?: string;
}


const API_BASE = process.env.REACT_APP_API_URL ?? '';

const KIND_NOUN: Record<PaywallPayload['quota_kind'], string> = {
  agent_request: 'agent build',
  consultant_session: 'consultant session',
  team_seat: 'team seat',
};

const KIND_EYEBROW: Record<PaywallPayload['quota_kind'], string> = {
  agent_request: 'Quota reached · builds',
  consultant_session: 'Quota reached · sessions',
  team_seat: 'Quota reached · seats',
};


function formatPriceCents(cents: number | null | undefined): string {
  if (cents == null) return '';
  const dollars = cents / 100;
  return dollars % 1 === 0 ? `$${dollars}` : `$${dollars.toFixed(2)}`;
}


/* What both the card and the pop-up do: the plan change and the one-time
 * top-up, and whether this person may spend on the plan at all. */
function usePaywallActions(
  payload: PaywallPayload,
  onTopUpSuccess?: (creditId: string | null) => void,
  onDismiss?: () => void,
) {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const [topUpLoading, setTopUpLoading] = useState(false);
  const [topUpError, setTopUpError] = useState<string | null>(null);
  const [topUpKey] = useState(() => crypto.randomUUID());

  const noun = KIND_NOUN[payload.quota_kind];
  const eyebrow = KIND_EYEBROW[payload.quota_kind];
  const upgradeTarget = payload.upgrade_to;
  const canTopUp = payload.top_up.available && payload.top_up.unit_price_cents;
  // Both actions here spend money against the plan, which is the Owner's.
  const canBill = can('manage_client_billing');
  const topUpPriceLabel = formatPriceCents(payload.top_up.unit_price_cents);

  async function handleTopUp() {
    if (!canTopUp || topUpLoading) return;
    setTopUpLoading(true);
    setTopUpError(null);
    try {
      const res = await axios.post(`${API_BASE}/api/billing/top-up`, {
        kind: payload.quota_kind,
        quantity: 1,
        idempotency_key: topUpKey,
      });
      const data = res.data as { credit_id?: string | null };
      onTopUpSuccess?.(data.credit_id ?? null);
    } catch (err) {
      const error = err as AxiosError<{ detail?: { message?: string } | string }>;
      const detail = error.response?.data?.detail;
      const msg =
        typeof detail === 'string'
          ? detail
          : detail?.message || 'Could not complete the purchase. Try again.';
      setTopUpError(msg);
    } finally {
      setTopUpLoading(false);
    }
  }

  function handleUpgrade() {
    if (upgradeTarget === 'workspace') {
      onDismiss?.();
      navigate('/settings?tab=payment');
      return;
    }
    const qs = new URLSearchParams({
      from: 'paywall',
      kind: payload.quota_kind,
      ...(upgradeTarget ? { tier: upgradeTarget } : {}),
    });
    onDismiss?.();  // clear the paywall card before leaving for billing
    navigate(`/client/billing?${qs.toString()}`);
  }

  return {
    noun, eyebrow, upgradeTarget, canTopUp, canBill, topUpPriceLabel,
    topUpLoading, topUpError, handleTopUp, handleUpgrade,
  };
}

const paywallTitle = (payload: PaywallPayload, noun: string) => {
  if (payload.limit > 0) {
    return `${payload.current_usage} of ${payload.limit} ${noun}${payload.limit !== 1 ? 's' : ''} used on ${payload.plan_display_name}.`;
  }
  return `No ${noun}s included on ${payload.plan_display_name}.`;
};

export function PaywallCard({
  payload,
  onTopUpSuccess,
  onDismiss,
  className = '',
}: PaywallCardProps) {
  const {
    noun, eyebrow, upgradeTarget, canTopUp, canBill, topUpPriceLabel,
    topUpLoading, topUpError, handleTopUp, handleUpgrade,
  } = usePaywallActions(payload, onTopUpSuccess, onDismiss);

  return (
    <div
      role="alertdialog"
      aria-live="polite"
      className={`aa-paywall ${className}`}
    >
      <div className="aa-paywall-head">
        <span className="aa-paywall-eyebrow">{eyebrow}</span>
        {onDismiss && (
          <button
            type="button"
            onClick={onDismiss}
            aria-label="Dismiss"
            className="aa-paywall-dismiss"
          >
            <X size={14} strokeWidth={2} />
          </button>
        )}
      </div>

      <h3 className="aa-paywall-title">{paywallTitle(payload, noun)}</h3>

      <p className="aa-paywall-body">{payload.message}</p>

      {/* Only the Owner holds `manage_client_billing`, and the billing page is
          open to nobody else. A champion or a department head can reach a
          paywall (inviting somebody takes a seat), so offering them an upgrade
          button would send them to a page the route guard turns away, landing
          them on their dashboard with the seat still unbought. They get told
          who can do it instead. */}
      {!canBill && (
        <p className="aa-paywall-body">Your Owner can change the plan or add a seat.</p>
      )}

      <div className="aa-paywall-actions">
        {canBill && upgradeTarget && (
          <button
            type="button"
            onClick={handleUpgrade}
            className="aa-paywall-primary"
          >
            <Sparkles size={14} strokeWidth={2.5} />
            <span>
              {upgradeTarget === 'workspace' ? 'Move to the Team plan' : <>Upgrade to{' '}
              <span style={{ textTransform: 'capitalize' }}>{upgradeTarget}</span></>}
            </span>
            <ChevronRight size={14} strokeWidth={2.5} />
          </button>
        )}

        {canBill && canTopUp && (
          <button
            type="button"
            onClick={handleTopUp}
            disabled={topUpLoading}
            className="aa-paywall-secondary"
          >
            {topUpLoading
              ? 'Charging…'
              : `Add 1 for ${topUpPriceLabel}`}
          </button>
        )}
      </div>

      {topUpError && (
        <p className="aa-paywall-error" role="alert">{topUpError}</p>
      )}
    </div>
  );
}

/* The paywall as a pop-up in the house theme, for a gate the person hit by
 * acting (inviting someone). */
export function PaywallModal({
  payload,
  onTopUpSuccess,
  onDismiss,
}: PaywallCardProps & { onDismiss: () => void }) {
  const {
    noun, upgradeTarget, canTopUp, canBill, topUpPriceLabel,
    topUpLoading, topUpError, handleTopUp, handleUpgrade,
  } = usePaywallActions(payload, onTopUpSuccess, onDismiss);

  let upgradeLabel = 'See plans';
  if (upgradeTarget === 'workspace') upgradeLabel = 'Move to the Team plan';
  if (upgradeTarget && upgradeTarget !== 'workspace') upgradeLabel = `Upgrade to ${upgradeTarget.charAt(0).toUpperCase()}${upgradeTarget.slice(1)}`;

  const footer = (
    <div className="flex flex-wrap items-center justify-end gap-2">
      <Action variant="quiet" onClick={onDismiss} disabled={topUpLoading} reason="Working">
        {canBill ? 'Not now' : 'Close'}
      </Action>
      {canBill && canTopUp && (
        <Action variant="outline" onClick={handleTopUp} disabled={topUpLoading} reason="Working">
          {topUpLoading ? 'Charging' : `Add 1 for ${topUpPriceLabel}`}
        </Action>
      )}
      {canBill && upgradeTarget && (
        <Action onClick={handleUpgrade} arrow>{upgradeLabel}</Action>
      )}
    </div>
  );

  return (
    <Modal size="dialog" onClose={onDismiss} context={`${noun.charAt(0).toUpperCase()}${noun.slice(1)} limit reached`} footer={footer}>
      <h2 className="m-0 text-[22px] font-semibold leading-[30px] text-ink">{paywallTitle(payload, noun)}</h2>
      <p className="m-0 mt-1 text-[13.5px] leading-[22px] text-ink-dim">{payload.message}</p>
      {!canBill && (
        <p className="m-0 mt-3 text-[13.5px] leading-[22px] text-ink">Your Owner can change the plan or add a seat.</p>
      )}
      {topUpError && <p className="m-0 mt-3 text-[13px] text-[#B42318]" role="alert">{topUpError}</p>}
    </Modal>
  );
}

export default PaywallCard;
